import { z } from 'zod';

export const ATLAS_SAVED_VIEW_TYPE = 'saved-people-view' as const;
// v2: filters (scopeRules) only. displayRules and asOf are deferred — a
// saved view captures who is in the view, not how they're styled, and is
// live-only for now. See atlas-custom-views.md, Slice A.1.
export const ATLAS_SAVED_VIEW_DEFINITION_VERSION = 2 as const;

// Mirrors ConditionOperator in src/core/assistant/models/ViewRules.ts. Never
// trust a client-submitted operator string beyond this registered set.
const CONDITION_OPERATORS = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'greater_than',
  'less_than',
  'is_empty',
  'is_not_empty',
] as const;

const conditionSchema = z.object({
  id: z.string().trim().min(1).max(128),
  field: z.string().trim().min(1).max(256),
  operator: z.enum(CONDITION_OPERATORS),
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number(), z.boolean()])), z.null()])
    .default(null),
}).passthrough();

// A filter rule's display/styling config is never persisted here — a saved
// view captures who is in the view, not how they're styled (no display
// rules this release; see atlas-custom-views.md).
const ruleSchema = z.object({
  id: z.string().trim().min(1).max(128),
  name: z.string().trim().min(1).max(160),
  conditions: z.array(conditionSchema).max(10),
  logic: z.enum(['and', 'or']),
  enabled: z.boolean(),
  order: z.number().int().min(0).max(100),
}).passthrough();

export const AtlasSavedViewDefinitionSchema = z.object({
  version: z.literal(ATLAS_SAVED_VIEW_DEFINITION_VERSION),
  filterRules: z.array(ruleSchema).max(10),
  includeReportingChain: z.literal(true),
}).superRefine((value, context) => {
  const scalarValues = value.filterRules.flatMap((rule) => rule.conditions)
    .flatMap((condition) => Array.isArray(condition.value) ? condition.value : [condition.value])
    .filter((candidate) => candidate === null || ['string', 'number', 'boolean'].includes(typeof candidate));
  if (scalarValues.length > 100) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Too many scalar condition values' });
  }
  if (new TextEncoder().encode(JSON.stringify(value)).byteLength > 64 * 1024) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Definition exceeds 64 KB' });
  }
});

export type AtlasSavedViewDefinition = z.infer<typeof AtlasSavedViewDefinitionSchema>;

export interface AtlasSavedView {
  readonly id: string;
  readonly diagramId: string;
  readonly sourceFolderId: string;
  readonly sourceDiagramId: string;
  readonly definition: AtlasSavedViewDefinition;
  readonly revision: number;
  readonly status: 'creating' | 'active' | 'failed';
  readonly idempotencyKey: string;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedBy: string;
  readonly updatedAt: string;
}

export const CreateAtlasSavedViewSchema = z.object({
  sourceDiagramId: z.string().trim().min(1).max(256),
  name: z.string().trim().min(1).max(160),
  definition: AtlasSavedViewDefinitionSchema,
  idempotencyKey: z.string().trim().min(8).max(128),
}).strict();

export type CreateAtlasSavedViewInput = z.infer<typeof CreateAtlasSavedViewSchema>;

export const UpdateAtlasSavedViewSchema = z.object({
  definition: AtlasSavedViewDefinitionSchema,
  expectedRevision: z.number().int().min(1),
}).strict();

export type UpdateAtlasSavedViewInput = z.infer<typeof UpdateAtlasSavedViewSchema>;
